import { useState, useEffect } from 'react';

interface HeaderProps {
  startInDarkMode?: boolean;
  bannerHeightPercent?: number;
}

const Header = ({ startInDarkMode = true, bannerHeightPercent = 1 }: HeaderProps) => {
  const [isScrolled, setIsScrolled] = useState(!startInDarkMode);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    if (startInDarkMode) {
      const handleScroll = () => {
        const viewportHeight = window.innerHeight * bannerHeightPercent;
        setIsScrolled(window.scrollY > viewportHeight);
      };

      // Use passive listener for better scroll performance
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [startInDarkMode, bannerHeightPercent]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/10 backdrop-blur-xl border-b border-white/20 shadow-lg' 
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <a href='/' target='_self'>
                <img
                src={isScrolled 
                    ? "/images/logos/targetsoftware.png"
                    : "/images/logos/targetsoftware-white.png"
                }
                alt="Target Software"
                className="h-8 w-auto transition-all duration-300"
                width="200"
                height="32"
                loading="eager"
                fetchPriority="high"
                />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <div className="relative group">
              <a 
                href="/produtos"
                className={`transition-colors duration-300 flex items-center whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-[#161e2d]' : 'text-white hover:text-[#FFFFFF]'
                }`}
              >
                Produtos
                <i className="ri-arrow-down-s-line ml-1"></i>
              </a>
              <div className={`absolute top-full left-0 mt-2 w-64 rounded-xl shadow-lg border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ${
                isScrolled 
                  ? 'bg-white/90 backdrop-blur-md border-gray-100' 
                  : 'bg-white/10 backdrop-blur-xl border-white/20'
              }`}>
                <div className="p-2">
                  <a href="/produtos/target-incentivos" className={`block px-4 py-3 text-sm rounded-lg transition-colors cursor-pointer ${
                    isScrolled 
                      ? 'text-gray-700 hover:bg-gray-50' 
                      : 'text-white hover:bg-white/10'
                  }`}>
                    Target Incentivos
                  </a>
                  <a href="/produtos/target-partnership" className={`block px-4 py-3 text-sm rounded-lg transition-colors cursor-pointer ${
                    isScrolled 
                      ? 'text-gray-700 hover:bg-gray-50' 
                      : 'text-white hover:bg-white/10'
                  }`}>
                    Target Partnership
                  </a>
                  <a href="/produtos/target-sales" className={`block px-4 py-3 text-sm rounded-lg transition-colors cursor-pointer ${
                    isScrolled 
                      ? 'text-gray-700 hover:bg-gray-50' 
                      : 'text-white hover:bg-white/10'
                  }`}>
                    Target Sales
                  </a>
                  <a href="/produtos/inteligencia-dados" className={`block px-4 py-3 text-sm rounded-lg transition-colors cursor-pointer ${
                    isScrolled 
                      ? 'text-gray-700 hover:bg-gray-50' 
                      : 'text-white hover:bg-white/10'
                  }`}>
                    Inteligência de Dados
                  </a>                  
                </div>
              </div>
            </div>
            <a href="/cases" className={`transition-colors duration-300 whitespace-nowrap cursor-pointer ${
              isScrolled ? 'text-gray-700 hover:text-[#161e2d]' : 'text-white hover:text-[#FFFFFF]'
            }`}>
              Cases
            </a>
            <div className="relative group">
              <a 
                href="/tecnologia"
                className={`transition-colors duration-300 flex items-center whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-[#161e2d]' : 'text-white hover:text-[#FFFFFF]'
                }`}
              >
                Tecnologia
                <i className="ri-arrow-down-s-line ml-1"></i>
              </a>
              <div className={`absolute top-full left-0 mt-2 w-64 rounded-xl shadow-lg border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ${
                isScrolled 
                  ? 'bg-white/90 backdrop-blur-md border-gray-100' 
                  : 'bg-white/10 backdrop-blur-xl border-white/20'
              }`}>
                <div className="p-2">
                  <a href="/tecnologia/fabrica-software" className={`block px-4 py-3 text-sm rounded-lg transition-colors cursor-pointer ${
                    isScrolled 
                      ? 'text-gray-700 hover:bg-gray-50' 
                      : 'text-white hover:bg-white/10'
                  }`}>
                    Fábrica de Software
                  </a>
                  <a href="/tecnologia/outsourcing" className={`block px-4 py-3 text-sm rounded-lg transition-colors cursor-pointer ${
                    isScrolled 
                      ? 'text-gray-700 hover:bg-gray-50' 
                      : 'text-white hover:bg-white/10'
                  }`}>
                    Outsourcing
                  </a>
                </div>
              </div>
            </div>
            <a href="/contato" className={`transition-colors duration-300 whitespace-nowrap cursor-pointer ${
              isScrolled ? 'text-gray-700 hover:text-[#161e2d]' : 'text-white hover:text-[#FFFFFF]'
            }`}>
              Contato
            </a>
          </nav>

          <button
            className="lg:hidden p-2 cursor-pointer"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl transition-colors duration-300 ${
              isScrolled ? 'text-gray-700' : 'text-white'
            }`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className={`lg:hidden border-t transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md border-gray-100' 
            : 'bg-white/10 backdrop-blur-xl border-white/20'
        }`}>
          <div className="px-4 py-4 space-y-4">
            <div>
              <a 
                href="/produtos"
                className={`font-medium mb-2 block ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                Produtos
              </a>
              <div className="pl-4 space-y-2">
                <a href="/produtos/target-incentivos" className={`block text-sm cursor-pointer ${
                  isScrolled ? 'text-gray-600' : 'text-gray-200'
                }`}>
                  Target Incentivos
                </a>
                <a href="/produtos/target-partnership" className={`block text-sm cursor-pointer ${
                  isScrolled ? 'text-gray-600' : 'text-gray-200'
                }`}>
                  Target Partnership
                </a>
                <a href="/produtos/target-sales" className={`block text-sm cursor-pointer ${
                  isScrolled ? 'text-gray-600' : 'text-gray-200'
                }`}>
                  Target Sales
                </a>
                <a href="/produtos/inteligencia-dados" className={`block text-sm cursor-pointer ${
                  isScrolled ? 'text-gray-600' : 'text-gray-200'
                }`}>
                  Inteligência de Dados
                </a>
                <a href="/produtos/fabrica-software" className={`block text-sm cursor-pointer ${
                  isScrolled ? 'text-gray-600' : 'text-gray-200'
                }`}>
                  Fábrica de Software
                </a>
                <a href="/produtos/outsourcing" className={`block text-sm cursor-pointer ${
                  isScrolled ? 'text-gray-600' : 'text-gray-200'
                }`}>
                  Outsourcing
                </a>
              </div>
            </div>
            <a href="/cases" className={`block cursor-pointer ${
              isScrolled ? 'text-gray-700' : 'text-white'
            }`}>
              Cases
            </a>
            <div>
              <a 
                href="/tecnologia"
                className={`font-medium mb-2 block ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                Tecnologia
              </a>
              <div className="pl-4 space-y-2">
                <a href="/tecnologia/fabrica-software" className={`block text-sm cursor-pointer ${
                  isScrolled ? 'text-gray-600' : 'text-gray-200'
                }`}>
                  Fábrica de Software
                </a>
                <a href="/tecnologia/outsourcing" className={`block text-sm cursor-pointer ${
                  isScrolled ? 'text-gray-600' : 'text-gray-200'
                }`}>
                  Outsourcing
                </a>
              </div>
            </div>
            <a href="/contato" className={`block cursor-pointer ${
              isScrolled ? 'text-gray-700' : 'text-white'
            }`}>
              Contato
            </a>
            <a
              href="https://www.linkedin.com/company/targetsoftware/jobs/"
              target="_blank"
              rel="noopener noreferrer"
              className={`block cursor-pointer ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              Trabalhe Conosco
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;